<template>
  <div class="pt-4">
    <div class="inner py-0">
      <h1>Carrier Group Mapping</h1>
    </div>

    <div class="inner flex items-center justify-between gap-4 py-2">
      <div class="flex items-center gap-4">
        <div class="join justify-center">
          <input type="radio" name="carrierOption" aria-label="Issuing Companies"
                 class="join-item btn btn-sm"
                 @click="mode = 'issuingCompanies'"
                 :checked="mode === 'issuingCompanies'" />

          <input type="radio" name="carrierOption" aria-label="Carriers"
                 class="join-item btn btn-sm"
                 @click="mode = 'carriers'"
                 :checked="mode === 'carriers'" />
        </div>

        <CreateCarrierModal v-if="mode === 'carriers'" @created="carrierCreated" />
      </div>

      <label class="search-bar input input-sm flex min-w-96 items-center gap-2">
        <input
          ref="searchInputRef"
          type="text"
          placeholder="Search"
          class="grow"
          v-model="filterText"
          autocomplete="off"
          @keyup="handleSearchKeyUp" />
        <XIcon
          v-if="filterText && filterText.length > 0"
          :size="20"
          class="cursor-pointer"
          @click="filterText = ''" />
        <SearchIcon :size="20" />
      </label>
    </div>

    <div class="inner pt-0">

    <Alert v-if="!loadingData && error" type="error">{{ error }}</Alert>

    <div v-visible="isError" class="toast toast-top toast-center z-2000">
      <div class="alert bg-primary text-white">
        <span>{{ errorMessage }}</span>
        <app-button @click="errorMessage = undefined" class="text-xs">OK</app-button>
      </div>
    </div>

    <Skeleton v-if="loadingData && !error" shape="rectangle" width="100%" height="300px" class="rounded-md" />
    <div v-else class="flex flex-wrap gap-0">

      <table v-if="mode === 'issuingCompanies'" v-tablesort id="issuing-companies-table" class="apptable dense mt-2">
        <thead>
        <tr>
          <td class="text-left sorted ascending">Issuing Company</td>
          <td class="country">Carrier Group</td>
          <td>AM Best Company</td>
          <td>AM Best Parent Company</td>
          <td class="nosort"></td>
        </tr>
        </thead>

        <tbody>
        <tr v-for="ic in filteredIssuingCompanyList" :key="ic.issuingCompanyId">
          <td>{{ ic.issuingCompanyName }}</td>
          <td>{{ ic.insurerGroup }}</td>
          <td>{{ ic.amBestCompanyName }}</td>
          <td>{{ ic.ultimateParentName }}</td>
          <td>
            <div class="flex justify-end">
              <app-button class="rounded-full" @click="editIssuingCompany(ic)">
                <Edit2 :size="16" />
              </app-button>
            </div>
          </td>
        </tr>
        </tbody>
      </table>

      <table v-else-if="mode === 'carriers'" v-tablesort id="carriers-table" class="apptable dense mt-2">
        <thead>
        <tr>
          <td>Carrier</td>
          <td>Short Name</td>
          <td class="nosort"></td>
        </tr>
        </thead>

        <tbody>
        <tr v-for="c in filteredCarrierList" :key="c.ID">
          <td>{{ c.name }}</td>
          <td>{{ c.shortName }}</td>
          <td>
            <div class="flex justify-end">
              <app-button class="rounded-full" @click="editCarrier(c)">
                <Edit2 :size="16" />
              </app-button>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
    </div>

    <AppModal
      :show="showDialog"
      size="lg"
      cancel-button-label="Cancel"
      action-button-label="Save"
      :handle-cancel="closeDialog"
      :handle-action="onMapSave"
      :action-disabled="saving">
      <template #title>
        <h1>Editing {{ selected ? (editingIssuingCompany ? selected.issuingCompanyName : selected.name) : '...' }}</h1>
      </template>
      <template #content>
        <div v-if="selected" class="mt-4 ml-2 space-y-4">
          <Combobox
            v-if="editingIssuingCompany"
            v-model="selected.insurerGroup"
            label="Carrier Group"
            :options="carrierOptions"
            allow-free-text
            data-test="carrier-group-combo" />

          <TextField
            v-if="editingCarrier"
            v-model="rename"
            label="Carrier Name"
            data-test="rename-group-input"
            :error-message="editingCarrierError"
            :error="!!editingCarrierError" />

          <TextField
            v-if="editingCarrier"
            v-model="renameShort"
            label="Short Name"
            data-test="rename-group-input-short"
            :error-message="editingCarrierShortNameError"
            :error="!!editingCarrierShortNameError" />

          <Combobox
            v-if="editingIssuingCompany"
            v-model="selected.amBestCompanyName"
            label="AM Company Name"
            :options="amBestCompanyOptions"
            allow-free-text
            data-test="ultimate-parent-group-combo" />
        </div>

        <div
          v-if="isNewCarrierGroup"
          class="mx-auto flex min-h-20 items-center gap-4 px-6 text-sm text-gray-600">
          <MessageSquareWarning />
          A new carrier group "{{ selected.insurerGroup }}" will be created and will be associated to this issuing company.
        </div>
        <div
          v-else
          class="min-h-20"></div>
      </template>
    </AppModal>
  </div>
</template>

<script>
import { carrierService } from '@/services/api';
import { adminService } from '@/services/AdminService';
import CreateCarrierModal from '@/components/CreateCarrierModal';
import AppButton from '@/components/v2/AppButton.vue';
import AppModal from '@/components/v2/AppModal.vue';
import Alert from '@/components/v2/Alert.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import Combobox from '@/components/v2/Combobox.vue';
import TextField from '@/components/v2/TextField.vue';
import { Edit2, SearchIcon, XIcon, MessageSquareWarning } from '@lucide/vue';

const IssuingCompanyNameKey = 'issuingCompanyName',
  InsurerGroupKey = 'insurerGroup';

export default {
  name: 'CarrierGroupMapping',
  components: {
    AppButton,
    AppModal,
    Alert,
    Skeleton,
    Combobox,
    TextField,
    CreateCarrierModal,
    Edit2,
    SearchIcon,
    XIcon,
    MessageSquareWarning,
  },
  data: () => ({
    mode: 'issuingCompanies',
    loadingData: true,
    editingIssuingCompany: false,
    editingCarrier: false,
    editingCarrierError: '',
    editingCarrierShortNameError: '',
    showTable: true,
    saving: false,
    error: undefined,
    filterText: '',
    list: [],
    carrierList: [],
    carriers: [],
    amBestCompanies: [],
    selected: undefined,
    selectedID: undefined,
    rename: '',
    renameShort: '',
    originalGroup: '',
    originalGroupShortName: '',
    errorMessage: undefined,
    issuingCompanyNameKey: IssuingCompanyNameKey,
    insurerGroupKey: InsurerGroupKey,
    sortAsc: true,
    sortKey: IssuingCompanyNameKey,
    dialog: false,
    amBestParents: [],
    showSaveError: false,
    saveError: '',
  }),
  computed: {
    showDialog() {
      return this.editingIssuingCompany || this.editingCarrier;
    },
    amBestCompanyNames() {
      return this.amBestCompanies.map(c => c.ambCompanyName).sort((c1, c2) => c1.localeCompare(c2));
    },
    amBestCompanyOptions() {
      return this.amBestCompanyNames.map(label => ({ label, value: label }));
    },
    carrierOptions() {
      return (this.carriers || []).map(name => ({ label: name, value: name }));
    },
    isNewCarrierGroup() {
      return this.editingIssuingCompany && this.selected?.insurerGroup && !this.carriers.includes(this.selected.insurerGroup);
    },
    isError() {
      return this.errorMessage !== '' && this.errorMessage !== undefined;
    },
    filteredIssuingCompanyList() {
      let result = this.list;

      if (this.filterText) {
        result = result.filter(item => {
          for (const part of this.filterText.toLowerCase().split(' ')) {
            if (item.issuingCompanyName.toLowerCase().includes(part)) {
              return true;
            }
          }
          return false;
        });
      }

      // Sort so that undefined carrier groups are at the top
      return [...result].sort((a, b) => {
        const aUndefined = this.isUndefinedCarrierGroup(a.insurerGroup);
        const bUndefined = this.isUndefinedCarrierGroup(b.insurerGroup);
        if (aUndefined && !bUndefined) return -1;
        if (!aUndefined && bUndefined) return 1;
        return 0;
      });
    },
    filteredCarrierList() {
      if (!this.filterText) {
        return this.carrierList;
      }

      return this.carrierList.filter(item => {
        for (const part of this.filterText.toLowerCase().split(' ')) {
          if (item.name.toLowerCase().includes(part)) {
            return true;
          }
        }
        return false;
      });
    },
  },
  methods: {
    isUndefinedCarrierGroup(carrierGroup) {
      return carrierGroup === 'Undefined';
    },
    editIssuingCompany(ic) {
      this.editingIssuingCompany = true;
      this.editingCarrier = false;
      this.selected = { ...ic };
      this.selectedID = ic.issuingCompanyId;
      this.originalGroup = ic.insurerGroup;
      this.originalGroupShortName = ic.insurerGroupShortName;
    },
    editCarrier(c) {
      this.editingIssuingCompany = false;
      this.editingCarrier = true;
      this.selected = { ...c };
      this.selectedID = c.issuingCompanyId;
      this.rename = c.name;
      this.renameShort = c.shortName;
    },
    async closeDialog() {
      this.editingCarrier = false;
      this.editingIssuingCompany = false;
    },
    async refreshData() {
      this.loadingData = true;
      const [carriers, amBestCompanies, amBestParents, list] = await Promise.all([
        carrierService.list(),
        adminService.fetchAMBestCompanies(),
        adminService.fetchAMBestParents(),
        adminService.LoadCarrierIssuerList()
      ]);

      this.carriers = (carriers || []).map(item => item.name);
      this.carrierList = (carriers || []);
      this.list = list || [];
      this.amBestParents = amBestParents || [];
      this.amBestCompanies = amBestCompanies || [];

      this.sort(this.sortKey, this.sortAsc);
      this.loadingData = false;
    },
    onMapSave() {
      this.saving = true;
      setTimeout(async () => {
        if (this.editingCarrier) {
          const carrierInput = this.rename.trim();

          if (this.selected.name !== carrierInput) {
            const existing = this.carriers.find(item => item === carrierInput);
            if (existing) {
              this.editingCarrierError = 'Cannot rename to an existing Carrier Group';
              this.saving = false;
              return;
            }
          }

          if (carrierInput === '') {
            this.editingCarrierError = 'Name cannot be empty';
            this.saving = false;
            return;
          }

          if (this.renameShort && this.renameShort.trim().length > 30) {
            this.editingCarrierShortNameError = 'Short Name must be 30 characters or less.';
            this.saving = false;
            return;
          }

          const req = {
            insurerGroupName: this.rename,
            insurerGroupShortName: this.renameShort?.trim(),
          };

          await adminService.updateCarrier(this.selected.ID, req);

          this.saving = false;
          this.editingCarrier = false;

          await this.refreshData();

          return;
        }

        if (this.editingIssuingCompany) {
          try {

            let insurerGroupId = this.carrierList.find(c => c.name === this.selected.insurerGroup)?.ID;

            // if insurer group doesn't exist, create a new one

            if (!insurerGroupId) {
              insurerGroupId = await adminService.CreateCarrier({
                insurerGroupName: this.selected.insurerGroup.trim(),
                issuingCompanyId: this.selected.issuingCompanyId,
              });
            }

            const req = {
              insurerGroupId: insurerGroupId,
              amBestNumber: this.amBestCompanies.find(c => c.ambCompanyName === this.selected.amBestCompanyName)?.ambNumber
            };

            await adminService.updateIssuingCompany(this.selected.issuingCompanyId, req);
            await this.refreshData();

            this.selected = {};
            this.rename = '';
            this.renameShort = '';

            this.editingIssuingCompany = false;
          } catch (err) {
            console.error(err);
            this.setErrorMessage('Unable to make this change');
          }
          this.saving = false;
        }
      }, 10);
    },
    setErrorMessage(msg) {
      this.rename = '';
      this.errorMessage = msg;
      setTimeout(() => {
        this.errorMessage = undefined;
      }, 4000);
    },
    handleSearchKeyUp(e) {
      if (e.key && e.key === 'Escape') {
        this.filterText = '';
        e.stopPropagation();
      }
    },
    sort(key, dir) {
      this.list.sort((a, b) => {
        const x = a[key].localeCompare(b[key]);
        return dir ? x : 0 - x;
      });
      this.sortKey = key;
      this.sortAsc = dir;
    },
    async carrierCreated(isError) {
      if (isError) {
        this.showSaveError = true;
        this.saveError = 'An unexpected error occurred.';
      } else {
        await this.refreshData();
      }
    },
  },
  mounted() {
    if (this.$refs.searchInputRef) {
      this.$refs.searchInputRef.focus();
    }
  },
  created() {
    this.refreshData();
  },
};
</script>

<style scoped>
.carrier-table-row > td > button {
  visibility: hidden;
}

.carrier-table-row:hover > td > button {
  visibility: visible;
}
</style>
